import { List } from 'immutable';

import type {
  AuthenticationBackendCreate,
  AuthenticationBackendUpdate,
} from 'stores/authentication/AuthenticationStore';
import type { SAMLBackendFormValues, SAMLConfigFormValues } from 'authentication/components/saml/config/types';
import type AuthenticationBackend from 'logic/authentication/AuthenticationBackend';
import type { EncryptedFieldValue } from 'components/configurationforms/types';

type SAMLCredentials = {
  private_key: EncryptedFieldValue<string>;
  certificate: string;
};

type SAMLConfigPayload = {
  type: string;
  idp_metadata_url: string;
  idp_metadata_refresh: 'disabled' | 'at_login';
  idp_sso_service_redirect_binding_url: string;
  idp_sso_service_post_binding_url: string;
  idp_logout_service_redirect_binding_url: string;
  idp_logout_service_post_binding_url: string;
  idp_entity_id: string;
  idp_certificates: string[];
  idp_user_name_attribute?: string;
  idp_email_attribute?: string;
  idp_first_name_attribute?: string;
  idp_last_name_attribute?: string;
  idp_full_name_attribute?: string;
  sp_base_url: string;
  sp_entity_id: string;
  sp_credentials: SAMLCredentials[];
};

export type SAMLTeamSyncBackendConfig = {
  type: string;
  group_attribute_name?: string;
  allowed_groups?: string[];
};

// We are using a custom type for the team sync config because GroupSyncBackend.ts is messy and mixes
// fields for different team sync backends.
export type SAMLTeamSyncBackend = {
  id: string;
  auth_service_backend_id: string;
  selection_type: string;
  selection: string[];
  default_roles?: string[];
  config: SAMLTeamSyncBackendConfig;
};

export type CreatePayload = AuthenticationBackendCreate & {
  default_roles: List<string>;
  config: SAMLConfigPayload;
};

export type UpdatePayload = AuthenticationBackendUpdate & {
  default_roles: List<string>;
  config: SAMLConfigPayload;
};

export type Payload = CreatePayload | UpdatePayload;

const buildSpCredentials = (values: SAMLConfigFormValues): SAMLCredentials[] => {
  // The backend already supports multiple SP credentials, the UI only one for now.
  if (values.spCredentialCertificate && values.spCredentialPrivateKey) {
    return [
      {
        // is_set is the value we get from the API. If that didn't change, we need to send keep_value. Otherwise,
        // we set a new value.
        private_key: values.spCredentialPrivateKey?.is_set ? { keep_value: true } : values.spCredentialPrivateKey,
        certificate: values.spCredentialCertificate,
      },
    ];
  }

  return [];
};

/**
 * Converts the config form values to the "config" part of an authentication backend service JSON payload.
 * @param values the form values to convert
 */
export const toConfigJSON = (values: SAMLConfigFormValues): SAMLConfigPayload => ({
  type: values.type,
  // Set empty URL values as undefined to avoid sending empty strings to the backend.
  idp_metadata_url: values.idpMetadataUrl || undefined,
  idp_metadata_refresh: values.idpMetadataRefreshAtLogin ? 'at_login' : 'disabled',
  idp_sso_service_redirect_binding_url: values.idpSsoServiceRedirectBindingUrl || undefined,
  idp_sso_service_post_binding_url: values.idpSsoServicePostBindingUrl || undefined,
  idp_logout_service_redirect_binding_url: values.idpLogoutServiceRedirectBindingUrl || undefined,
  idp_logout_service_post_binding_url: values.idpLogoutServicePostBindingUrl || undefined,
  idp_entity_id: values.idpEntityId,
  // The backend already supports multiple IdP certificates, the UI only one for now.
  idp_certificates: values.idpCertificate ? [values.idpCertificate] : [],
  // Set empty attribute values as undefined to avoid sending empty strings to the backend.
  idp_user_name_attribute: values.idpUserNameAttribute || undefined,
  idp_email_attribute: values.idpEmailAttribute || undefined,
  idp_first_name_attribute: values.idpFirstNameAttribute || undefined,
  idp_last_name_attribute: values.idpLastNameAttribute || undefined,
  idp_full_name_attribute: values.idpFullNameAttribute || undefined,
  sp_base_url: values.spBaseUrl || undefined,
  sp_entity_id: values.spEntityId,
  sp_credentials: buildSpCredentials(values),
});

export const fromAuthenticationBackend = (
  backend: AuthenticationBackend,
  teamSyncBackend: SAMLTeamSyncBackend | undefined,
): SAMLBackendFormValues => {
  const config = backend.config as unknown as SAMLConfigFormValues;

  if (teamSyncBackend?.config) {
    config.teamSyncIsCurrentlyEnabled = true;
    config.teamSyncEnabled = true;
    config.teamSyncGroupAttributeName = teamSyncBackend.config?.group_attribute_name;
    config.teamSyncAllowedGroups = teamSyncBackend.config?.allowed_groups.join(',');
  } else {
    config.teamSyncIsCurrentlyEnabled = false;
    config.teamSyncEnabled = false;
    config.teamSyncAllowedGroups = undefined;
  }

  return {
    id: backend.id,
    title: backend.title,
    description: backend.description,
    defaultRoles: backend.defaultRoles.join(','),
    ...config,
  };
};

/**
 * Converts the "config" part of the authentication backend service JSON payload to form values.
 * @param payload the config payload to parse
 */
export const fromConfigJSON = (payload: SAMLConfigPayload): SAMLConfigFormValues => ({
  type: payload.type,
  idpMetadataUrl: payload.idp_metadata_url,
  idpMetadataRefreshAtLogin: payload.idp_metadata_refresh === 'at_login',
  idpSsoServiceRedirectBindingUrl: payload.idp_sso_service_redirect_binding_url,
  idpSsoServicePostBindingUrl: payload.idp_sso_service_post_binding_url,
  idpLogoutServiceRedirectBindingUrl: payload.idp_logout_service_redirect_binding_url,
  idpLogoutServicePostBindingUrl: payload.idp_logout_service_post_binding_url,
  idpEntityId: payload.idp_entity_id,
  // The backend already supports multiple IdP certificates, the UI only one for now.
  idpCertificate: payload.idp_certificates?.[0],
  idpCertificateFilename: payload.idp_certificates?.[0] && 'idp-certificate.pem',
  idpCertificateInfo: undefined,
  idpCertificateInfoError: undefined,
  idpUserNameAttribute: payload.idp_user_name_attribute,
  idpEmailAttribute: payload.idp_email_attribute,
  idpFirstNameAttribute: payload.idp_first_name_attribute,
  idpLastNameAttribute: payload.idp_last_name_attribute,
  idpFullNameAttribute: payload.idp_full_name_attribute,
  spBaseUrl: payload.sp_base_url,
  spEntityId: payload.sp_entity_id,
  // The backend already supports multiple SP credentials, the UI only one for now.
  spCredentialCertificate: payload.sp_credentials?.[0]?.certificate,
  spCredentialCertificateFilename: payload.sp_credentials?.[0].certificate && 'sp-certificate.pem',
  spCredentialCertificateInfo: undefined,
  spCredentialCertificateInfoError: undefined,
  spCredentialPrivateKey: payload.sp_credentials?.[0]?.private_key,
  spCredentialPrivateKeyFilename: payload.sp_credentials?.[0]?.private_key.is_set && 'sp-private-key.pem',
  teamSyncIsCurrentlyEnabled: false,
  teamSyncEnabled: false,
  teamSyncGroupAttributeName: undefined,
  teamSyncAllowedGroups: undefined,
});

/**
 * Converts the full backend form values (title, description, config, etc.) to an authentication service JSON payload.
 * @param values the backend form values to convert.
 */
export const toBackendJSON = (values: SAMLBackendFormValues): Payload => ({
  id: values.id,
  title: values.title,
  description: values.description,
  default_roles: List(values.defaultRoles.split(',')),
  config: toConfigJSON(values),
});
